/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-restricted-globals */

import React, { useEffect, useState } from 'react';
import moment from 'moment';
import Cookies from 'js-cookie';
import { PayPalScriptProvider } from '@paypal/react-paypal-js';
import NumberFormat from 'react-number-format';
import Slider from 'react-slick';

import api from '../../../api/api';
import PayPalButtons from '../../../components/PayPalButtons/PayPalButtons';
import PayPalErrorBoundary from '../../../components/PayPalErrorBoundary/PayPalErrorBoundary';
import usePayPalAvailability from '../../../hooks/usePayPalAvailability';
import Reviews from '../../../components/Reviews/Reviews';

import {
  buildQueryParams,
  getPriceWithCurrency,
  getSavingsPercent,
  makePlural,
} from '../../../utils/helpers';
import { states } from '../../../utils/states';

// Import styles
import styles from './Red.module.scss';

// Import images (use require or import)
import slide1 from './img/slide-1.webp';
import slide2 from './img/slide-2.webp';
import slide3 from './img/slide-3.webp';
import slide4 from './img/slide-4.webp';
import hookImg from './img/hook.webp';
import shippingProtectionImg from './img/shipping_protection.webp';
import visaImg from './img/visa.svg';
import mastercardImg from './img/mastercard.svg';
import amexImg from './img/amex.svg';
import paypalImg from './img/paypal.svg';
import checkoutSecuredImg from './img/checkout-secured.webp';
import guaranteeImg from './img/guarantee.svg';

// --- Helper Functions (Adapted from Default.jsx) ---

function getFbcValue(queryParams) {
  if (Cookies.get('_fbc')) {
    return Cookies.get('_fbc');
  }
  if (queryParams.fbclid) {
    const timestamp = new Date().getTime();
    return `fb.1.${timestamp}.${queryParams.fbclid}`;
  }
  return '';
}

const validationMessages = {
  email: 'Enter valid email address.',
  phone: 'Enter valid phone number.',
  creditCardNumber: 'Enter valid credit card number.',
  firstName: 'First name is required.',
  lastName: 'Last name is required.',
  shippingAddress1: 'Shipping address is required.',
  shippingCity: 'City is required.',
  shippingState: 'This Field is required.',
  shippingZip: 'ZIP code is required.',
  expirationMonth: 'Select a valid expiration month.',
  expirationYear: 'Select a valid expiration year.',
  CVV: 'Enter valid CVV (3 or 4 digits).',
};

// Validate a single field (Simplified for Red template structure)
function validateField(fieldName, value, shippingCountry) {
  switch (fieldName) {
    case 'email':
      return !value || !/\S+@\S+\.\S+/.test(value) ? validationMessages.email : null;
    case 'phone':
      return !value ? validationMessages.phone : null;
    case 'creditCardNumber':
      return !value || !/^\d{13,19}$/.test(value.replace(/\s+/g, ''))
        ? validationMessages.creditCardNumber
        : null;
    case 'firstName':
      return !value ? validationMessages.firstName : null;
    case 'lastName':
      return !value ? validationMessages.lastName : null;
    case 'shippingAddress1':
      return !value ? validationMessages.shippingAddress1 : null;
    case 'shippingCity':
      return !value ? validationMessages.shippingCity : null;
    case 'shippingState':
      return !value ? validationMessages.shippingState : null;
    case 'shippingZip':
      return !value ? validationMessages.shippingZip : null;
    case 'expirationMonth':
      return !value ? validationMessages.expirationMonth : null;
    case 'expirationYear':
      return !value ? validationMessages.expirationYear : null;
    case 'CVV':
      return !value || !/^\d{3,4}$/.test(value) ? validationMessages.CVV : null;
    default:
      return null;
  }
}

// Add this new function for displaying error messages
function renderErrorMessage(fieldName, errors) {
  const error = errors[fieldName];
  return error ? <div className={styles['error__message']}>{error}</div> : null;
}

const validateForm = (data, shippingCountry) => {
  const errors = {};
  Object.keys(validationMessages).forEach((field) => {
    if (field !== 'expirationDate') {
      const error = validateField(field, data[field], shippingCountry);
      if (error) {
        errors[field] = error;
      }
    }
  });

  if (!errors.expirationMonth && !errors.expirationYear) {
    const { expirationMonth, expirationYear } = data;
    const expDate = moment(`${expirationMonth}-${expirationYear}`, 'MM-YYYY').endOf('month');
    const currentDate = moment();
    if (!expDate.isValid() || expDate.isBefore(currentDate, 'day')) {
      errors.expirationMonth = 'Enter a valid expiration date.';
    }
  }

  return errors;
};

// --- FAQ Data (Example - can be moved or fetched) ---
const faqData = [
  {
    question: 'What sizes do Compressics™ Knee Sleeves come in?',
    answer: `We guarantee they will fit you, or we'll ship you a Knee Sleeve-Plus replacement. In 99% of cases the fit is perfect, but should you have any issue simply contact us at support@compressics.com, or by phone at 1-855-979-7444 and we can ship you a different size for free!`,
  },
  {
    question: 'What kinds of knee pain does this help with?',
    answer: `If you are suffering from tendonitis, bursitis, osteoarthritis, rheumatoid arthritis, or any other joint discomfort, then the Compressics™ Sleeves could help.`,
  },
  {
    question: `Do these slip off the top of the knee during exercise?`,
    answer: `No. They have a specially designed gripping strip on the inside to hold the sleeve up regardless of how hard you move or exercise.`,
  },
  {
    question: 'Can they be worn all day like during work or only during workouts?',
    answer:
      'Compressics™ Knee Sleeves can be worn all day and during workouts, running and most other forms of exercise.',
  },
  {
    question: "What happens if this doesn't fit? Do you offer returns or refunds?",
    answer:
      'Yes! We offer a simple no-hassle returns and exchange process. Simply contact us at support@compressics.com, or by phone at 1-855-979-7444!',
  },
  {
    question: 'How long does delivery take?',
    answer: 'Delivery takes 3-7 days.',
  },
  {
    question: 'Where can I go to buy this in person?',
    answer:
      'In an effort to keep costs as low as possible for our customers, we currently sell our product exclusively online!',
  },
  {
    question: 'Can you wear the sleeves while sleeping?',
    answer:
      'Absolutely! Wearing them while sleeping could help reduce any leg swelling that might’ve occurred during the day when you’re more active. There is no need to wear them while sleeping however if you don’t experience any pain or swelling while sleeping.',
  },
  {
    question: 'Is the sleeve bulky when worn underneath my jeans?',
    answer:
      'The knee sleeves aren’t noticeable at all provided that the jeans or sweatpants you’re wearing aren’t too tight.',
  },
];

// --- Custom Slider Arrows ---
const ArrowStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 36,
  height: 36,
  background: '#111',
  borderRadius: 6,
  cursor: 'pointer',
  zIndex: 2,
  border: 'none',
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
};

function NextArrow(props) {
  const { style, onClick } = props;
  return (
    <button type='button' style={{ ...ArrowStyle, right: 8, ...style }} onClick={onClick} aria-label='Next'>
      <i className='fa-solid fa-chevron-right' style={{ color: '#fff', fontSize: 18 }} />
    </button>
  );
}

function PrevArrow(props) {
  const { style, onClick } = props;
  return (
    <button
      type='button'
      style={{ ...ArrowStyle, left: 8, ...style }}
      onClick={onClick}
      aria-label='Previous'
    >
      <i className='fa-solid fa-chevron-left' style={{ color: '#fff', fontSize: 18 }} />
    </button>
  );
}

// --- Component ---

export default function Red({
  offer,
  isLoading,
  crossSells,
  onCreateOrder,
  declineError,
  reviews,
  showReviews,
  isCooldownActive,
}) {
  const queryParams = buildQueryParams(location);
  const showPayPal = queryParams.paypal !== '0' && (queryParams.paypal === '1' || offer.showPayPal);
  const showApplePay = queryParams.applepay !== '0' && (queryParams.applepay === '1' || offer.showApplePay);
  const showGooglePay =
    queryParams.googlepay !== '0' && (queryParams.googlepay === '1' || offer.showGooglePay);

  const mergedProductsData = offer.productsData.map((product) => {
    const productDetail = offer.productDetails[product.id];

    return {
      ...product,
      ...productDetail,
    };
  });

  const defaultProduct =
    mergedProductsData.find((item) => item?.id === offer?.defaultVariantId) || mergedProductsData[0];
  const initialProduct = defaultProduct || mergedProductsData[0];

  const defaultVariantId = offer.defaultVariantId;
  const someProductIsSubscription = mergedProductsData.some((item) => item.billingModelId !== '2');

  // State Variables
  const [clientToken, setClientToken] = useState(null);
  const { isPayPalEnabled, isLoading: isPayPalLoading } = usePayPalAvailability(clientToken);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const selectedOfferType = 'subscription';
  const [addShippingGuarantee, setAddShippingGuarantee] = useState(false); // State for SafeGuard checkbox
  const [paymentMethod, setPaymentMethod] = useState('card'); // 'card' or 'paypal'
  const [errors, setErrors] = useState({});
  const [timeLeft, setTimeLeft] = useState(900); // 15 minutes in seconds
  const [activeFaqIndex, setActiveFaqIndex] = useState(null);
  const [state, setState] = useState({
    email: '',
    firstName: '',
    lastName: '',
    shippingAddress1: '',
    shippingCity: '',
    shippingState: 'AL',
    shippingCountry: offer?.shippingCountries[0]?.id || 'US',
    shippingZip: '',
    phone: '',
    creditCardNumber: '',
    expirationMonth: '',
    expirationYear: '',
    CVV: '',
    billingFirstName: '',
    billingLastName: '',
    billingAddress1: '',
    billingCity: '',
    billingState: '',
    billingCountry: 'US',
    billingZip: '',
    billingSameAsShipping: true,
    brand: offer.brand,
    isTermsAgreed: true,
    productId: initialProduct.id,
    productQty: initialProduct.quantity,
    productName: initialProduct.name,
    productPrice: initialProduct.price,
    agreeSms: false,
    fbc: getFbcValue(queryParams),
    fbp: Cookies.get('_fbp'),
    fbclid: queryParams.fbclid || '',
    orderTotal: initialProduct.price * 1 + initialProduct.shippingPrice * 1,
    campaignId: offer.campaignId,
    shippingId: initialProduct.shippingId || '',
    shippingPrice: initialProduct.shippingPrice || '',
    offers: [
      {
        product_id: initialProduct.id,
        offer_id: initialProduct.offerId,
        billing_model_id: initialProduct.billingModelId,
        price: initialProduct.price,
        productQty: initialProduct.quantity,
        productName: initialProduct.name,
        isMainProduct: true,
        ...(initialProduct?.trial?.enabled && {
          trial: {
            ...(initialProduct.trial.productId && { product_id: initialProduct.trial.productId }),
            ...(initialProduct.trial.price && { price: initialProduct.trial.price }),
            ...(initialProduct.trial.quantity && { quantity: initialProduct.trial.quantity }),
          },
        }),
      },
    ],
  });

  // --- Slider Nav State ---
  const [nav1, setNav1] = useState(null);
  const [nav2, setNav2] = useState(null);

  // Slider settings
  const sliderSettings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
    asNavFor: nav2,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
  };

  const sliderNavSettings = {
    slidesToShow: 4,
    slidesToScroll: 1,
    asNavFor: nav1,
    dots: false,
    arrows: false,
    centerMode: true, // Try enabling centerMode
    focusOnSelect: true,
    infinite: false, // Temporarily disable infinite scroll
    swipe: false,
    draggable: false,
    touchMove: false,
  };

  // --- Effects ---

  // Fetch Braintree token (if using express checkout)
  useEffect(() => {
    const merchantAccountId = offer.braintreeMerchantAccountId || offer.payPalMerchantAccountId;
    if ((showPayPal || showApplePay || showGooglePay) && merchantAccountId) {
      (async () => {
        try {
          const { data } = await api.get(`/braintree/token?merchantAccountId=${merchantAccountId}`);
          setClientToken(data.clientToken);
        } catch (err) {
          console.error('Failed to fetch Braintree token', err);
        }
      })();
    }
  }, [
    showPayPal,
    showApplePay,
    showGooglePay,
    offer.braintreeMerchantAccountId,
    offer.payPalMerchantAccountId,
  ]);

  useEffect(() => {
    const filteredProducts = filterProducts(mergedProductsData);
    const newSelectedProduct =
      filteredProducts.find(
        (item) =>
          item.quantity === selectedProduct?.quantity &&
          item.color === selectedVariant?.name &&
          (someProductIsSubscription
            ? item.billingModelId ===
              (selectedOfferType === 'subscription' ? selectedProduct?.billingModelId : '2')
            : true)
      ) || filteredProducts[0];

    if (
      newSelectedProduct &&
      newSelectedProduct?.id !== selectedProduct?.id &&
      newSelectedProduct.soldOut !== '1'
    ) {
      setSelectedProduct(newSelectedProduct);
    } else if (newSelectedProduct?.soldOut === '1') {
      setSelectedProduct(null);
    }
  }, [selectedOfferType, selectedVariant]);

  useEffect(() => {
    const stickyOffer = {
      billing_model_id: selectedProduct?.billingModelId,
      offer_id: selectedProduct?.offerId,
      product_id: selectedProduct?.id,
      price: selectedProduct?.price,
      productQty: selectedProduct?.quantity,
      productName: selectedProduct?.name,
      isMainProduct: true,
      ...(selectedProduct?.trial?.enabled && {
        trial: {
          ...(selectedProduct.trial.productId && { product_id: selectedProduct.trial.productId }),
          ...(selectedProduct.trial.price && { price: selectedProduct.trial.price }),
          ...(selectedProduct.trial.quantity && { quantity: selectedProduct.trial.quantity }),
        },
      }),
    };

    const currentOffers = state.offers || [];
    const newOffers = currentOffers.filter((offer) => !offer.isMainProduct);
    newOffers.push(stickyOffer);

    const offersTotal = newOffers.reduce((total, offer) => total + offer.price * 1, 0);
    const orderTotal = offersTotal + selectedProduct?.shippingPrice * 1;

    setState({
      ...state,
      orderTotal,
      offers: newOffers,
      shippingId: selectedProduct?.shippingId,
      shippingPrice: selectedProduct?.shippingPrice,
      productId: selectedProduct?.id,
      productQty: selectedProduct?.quantity,
      productName: selectedProduct?.name,
      productPrice: selectedProduct?.price,
    });
  }, [selectedProduct]);

  useEffect(() => {
    if (crossSells.length > 0) {
      // Get preselected cross-sells from offer config
      const preSelectedFromOffer = crossSells.filter((item) =>
        item.preselectedOffers.some((o) => o.id === offer._id)
      );

      // Get preselected cross-sells from URL parameter (supports comma-separated IDs)
      const urlCrossSellIds = queryParams.crosssellid
        ? queryParams.crosssellid.split(',').map((id) => id.trim())
        : [];
      const preSelectedFromUrl = crossSells.filter((item) => urlCrossSellIds.includes(String(item._id)));

      // Combine both sources, avoiding duplicates
      const preSelectedIds = new Set(preSelectedFromOffer.map((item) => item._id));
      const combinedPreSelected = [
        ...preSelectedFromOffer,
        ...preSelectedFromUrl.filter((item) => !preSelectedIds.has(item._id)),
      ];

      if (combinedPreSelected.length > 0) {
        const crossSellOffers = [];

        combinedPreSelected.forEach((item) => {
          let itemToAdd = item;

          if (item.variants && item.variants.length > 0) {
            itemToAdd = item.variants[0];
          }

          crossSellOffers.push({
            billing_model_id: itemToAdd.productBillingModelId,
            offer_id: itemToAdd.productOfferId,
            product_id: itemToAdd.productId,
            price: itemToAdd.price,
            productName: itemToAdd.productName || itemToAdd.name,
            productQty: '1',
            isCrossSell: true,
            ...(item.trial?.enabled && {
              trial: {
                product_id: item.trial.productId,
                price: item.trial.price,
                quantity: item.trial.quantity,
              },
            }),
          });
        });

        setState((prevState) => {
          const currentOffers = prevState.offers || [];
          const offersWithoutPreselectedCrossSells = currentOffers.filter(
            (offer) => !(offer.isCrossSell && offer.preselected)
          );
          const newOffers = [...offersWithoutPreselectedCrossSells, ...crossSellOffers];

          const offersTotal = newOffers.reduce((total, offer) => total + offer.price * 1, 0);
          const orderTotal = offersTotal + selectedProduct?.shippingPrice * 1;

          return {
            ...prevState,
            orderTotal,
            offers: newOffers,
          };
        });
      }
    }
  }, [crossSells]);

  useEffect(() => {
    const products = mergedProductsData;
    let defaultProduct = products[0];

    if (products?.length > 0) {
      setSelectedProduct(defaultProduct);
    }

    if (defaultVariantId) {
      defaultProduct = products.find((item) => item.id === defaultVariantId);
      defaultProduct && setSelectedProduct(defaultProduct);
    }

    if (defaultVariantId && !selectedVariant && defaultProduct) {
      handleVariantSelect({
        name: defaultProduct.color,
        imageSrc: defaultProduct.colorImage || defaultProduct.colorimage,
      });
    }

    if (products && defaultProduct) {
      setSelectedVariant(
        selectedVariant?.name
          ? selectedVariant
          : {
              name: defaultProduct.color,
              imageSrc: defaultProduct.colorImage || defaultProduct.colorimage,
            }
      );
    }
  }, [offer.productsData]);

  useEffect(() => {
    const anotherProductWithSameQuantity = mergedProductsData.find(
      (item) => item.quantity === selectedProduct?.quantity && item.id !== selectedProduct?.id
    );

    if (anotherProductWithSameQuantity) {
      setSelectedProduct(anotherProductWithSameQuantity);
    }
  }, [selectedOfferType]);

  // Countdown Timer
  useEffect(() => {
    if (timeLeft <= 0) return;
    const intervalId = setInterval(() => {
      setTimeLeft((prevTime) => prevTime - 1);
    }, 1000);
    return () => clearInterval(intervalId);
  }, [timeLeft]);

  useEffect(() => {
    if (state.shippingCountry === 'US' || state.shippingCountry === 'CA') {
      setState({ ...state, shippingState: 'AL' });
    } else {
      setState({ ...state, shippingState: '' });
    }
  }, [state.shippingCountry]);

  // Helpers

  function filterProductsByVariant(items) {
    if (!selectedVariant) {
      return items;
    }

    return items.filter((item) => item.color === selectedVariant.name);
  }

  function filterByOfferType(items) {
    if (someProductIsSubscription) {
      return items.filter((item) => {
        if (selectedOfferType === 'subscription') {
          return item.billingModelId !== '2';
        }

        return item.billingModelId === '2';
      });
    }

    return items;
  }

  function filterProducts(items) {
    const filteredByVariant = filterProductsByVariant(items);
    return filterByOfferType(filteredByVariant);
  }

  // Handle input changes for form fields
  function handleInputChange(e) {
    const { name, value } = e.target;
    setState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
    // Clear error when user types
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: null,
      ...(name === 'expirationMonth' || name === 'expirationYear' ? { expirationDate: null } : {}),
    }));
  }

  // Handle NumberFormat value changes
  function handleNumberFormatChange(values, name) {
    const { value } = values;
    setState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: null,
    }));
  }

  // Handlers

  function handleVariantSelect(variant) {
    setSelectedVariant(variant);
    const newProduct = mergedProductsData.find(
      (item) => item.color === variant.name && item.quantity === selectedProduct?.quantity
    );

    if (newProduct?.soldOut === '1') {
      setSelectedProduct(null);
    }

    setSelectedProduct(newProduct);
  }

  function handleFieldBlur(fieldName) {
    const valueToValidate = state[fieldName];
    const errorMessage = validateField(fieldName, valueToValidate, state.shippingCountry);

    setErrors((prevErrors) => ({
      ...prevErrors,
      [fieldName]: errorMessage,
    }));

    if (fieldName === 'expirationMonth' || fieldName === 'expirationYear') {
      const monthError = validateField('expirationMonth', state.expirationMonth, state.shippingCountry);
      const yearError = validateField('expirationYear', state.expirationYear, state.shippingCountry);
      if (!monthError && !yearError) {
        const expDate = moment(`${state.expirationMonth}-${state.expirationYear}`, 'MM-YYYY').endOf('month');
        const currentDate = moment();
        if (!expDate.isValid() || expDate.isBefore(currentDate, 'day')) {
          setErrors((prevErrors) => ({
            ...prevErrors,
            expirationMonth: 'Enter a valid expiration date.',
          }));
        } else {
          setErrors((prevErrors) => ({
            ...prevErrors,
            expirationMonth: null,
          }));
        }
      }
    }
  }

  function handleCrossSellChange(crossSell) {
    setState((prevState) => {
      const newOffers = [...prevState.offers];
      const existingItemIndex = newOffers.findIndex(
        (o) => o.product_id === crossSell.productId && o.isCrossSell
      );

      if (existingItemIndex > -1) {
        newOffers.splice(existingItemIndex, 1);
      } else {
        newOffers.push({
          billing_model_id: crossSell.productBillingModelId,
          offer_id: crossSell.productOfferId,
          product_id: crossSell.productId,
          price: crossSell.price,
          productName: crossSell.productName || crossSell.name,
          productQty: '1',
          isCrossSell: true,
          ...(crossSell.trial?.enabled && {
            trial: {
              product_id: crossSell.trial.productId,
              price: crossSell.trial.price,
              quantity: crossSell.trial.quantity,
            },
          }),
        });
      }

      const offersTotal = newOffers.reduce((total, offer) => total + parseFloat(offer.price || 0), 0);
      const shippingPrice = parseFloat(prevState.shippingPrice || 0);
      const orderTotal = offersTotal + shippingPrice;

      return {
        ...prevState,
        offers: newOffers,
        orderTotal,
      };
    });
  }

  function handleExpressApprove(data) {
    handleCreateOrder(data);
  }

  function handleFormSubmit() {
    if (isCooldownActive) return;

    const validationErrors = validateForm(state, state.shippingCountry);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length === 0) {
      handleCreateOrder(state);
    } else {
      const firstErrorField = Object.keys(validationErrors)[0];
      const fieldElement = document.querySelector(`[name="${firstErrorField}"]`);
      if (fieldElement) {
        fieldElement.focus();
      }
    }
  }

  function handleCreateOrder(data) {
    const stickyOffer = {
      billing_model_id: selectedProduct?.billingModelId,
      offer_id: selectedProduct?.offerId,
      product_id: selectedProduct?.id,
      price: selectedProduct?.price,
      productQty: selectedProduct?.quantity,
      productName: selectedProduct?.name,
      isMainProduct: true,
    };

    const currentOffers = state.offers || [];
    const newOffers = currentOffers.filter((offer) => !offer.isMainProduct);
    newOffers.push(stickyOffer);

    const offersTotal = newOffers.reduce((total, offer) => total + offer.price * 1, 0);
    const orderTotal = offersTotal + selectedProduct?.shippingPrice * 1;
    const expirationDate = `${state.expirationMonth}${state.expirationYear.slice(-2)}`;

    const orderData = {
      ...data,
      expirationDate,
      orderTotal,
      offers: newOffers,
      shippingId: selectedProduct?.shippingId,
      shippingPrice: selectedProduct?.shippingPrice,
      productId: selectedProduct?.id,
      productQty: selectedProduct?.quantity,
      productName: selectedProduct?.name,
      productPrice: selectedProduct?.price,
    };

    if (!selectedProduct) {
      setErrors({ ...errors, product: 'Please select a product' });
      return;
    } else {
      setErrors({ ...errors, product: null });
    }

    onCreateOrder(orderData);
  }

  const handleProductSelect = (product) => {
    setSelectedProduct(product);
  };

  const handlePaymentMethodChange = (e) => {
    setPaymentMethod(e.target.id === 'checkout-card' ? 'card' : 'paypal');
  };

  const handleFaqToggle = (index) => {
    setActiveFaqIndex(activeFaqIndex === index ? null : index);
  };

  // --- Main Render ---

  const formatTime = (timeInSeconds) => {
    const h = String(Math.floor(timeInSeconds / 3600)).padStart(2, '0');
    const m = String(Math.floor((timeInSeconds % 3600) / 60)).padStart(2, '0');
    const s = String(timeInSeconds % 60).padStart(2, '0');
    return `${h}:${m}:${s}`;
  };

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 10 }, (_, i) => currentYear + i);

  return (
    <div className={`${styles['red__template']} RedTemplate`}>
      {/* Header */}
      <header className={styles['header']}>
        <div className={styles['header__container']}>
          <img className={styles['header__logo']} src={offer.logoSrc} alt={offer.brand || 'Logo'} />
        </div>
      </header>

      <main>
        {/* Hero Section */}
        <section className={styles['hero']}>
          <div className={styles['container']}>
            <div className={styles['hero__container']}>
              <div className={styles['hero__wrap']}>
                <div className={styles['hero__left']}>
                  {/* Top Slider */}
                  <Slider {...sliderSettings} ref={setNav1} className={styles['hero__slider-top']}>
                    {/* Use offer.productImages or specific images */}
                    <div className={styles['hero__slide-top']}>
                      <img src={slide1} alt='Product View 1' />
                    </div>
                    <div className={styles['hero__slide-top']}>
                      <img src={slide2} alt='Product View 2' />
                    </div>
                    <div className={styles['hero__slide-top']}>
                      <img src={slide3} alt='Product View 3' />
                    </div>
                    <div className={styles['hero__slide-top']}>
                      <img src={slide4} alt='Product View 4' />
                    </div>
                  </Slider>
                  {/* Bottom Nav Slider */}
                  <Slider
                    {...sliderNavSettings}
                    ref={setNav2}
                    className={`${styles['hero__slider-bottom']} red-slider-bottom`}
                  >
                    <div className={styles['hero__slide-bottom']}>
                      <img src={slide1} alt='Thumbnail 1' />
                    </div>
                    <div className={styles['hero__slide-bottom']}>
                      <img src={slide2} alt='Thumbnail 2' />
                    </div>
                    <div className={styles['hero__slide-bottom']}>
                      <img src={slide3} alt='Thumbnail 3' />
                    </div>
                    <div className={styles['hero__slide-bottom']}>
                      <img src={slide4} alt='Thumbnail 4' />
                    </div>
                  </Slider>
                </div>
                <div className={styles['hero__right']}>
                  <div className={styles['hero__right-subtitle']}>
                    <ul className={styles['hero__right-stars']}>
                      {/* Render stars based on rating or statically */}
                      {[...Array(5)].map((_, i) => (
                        <li key={i}>
                          <i className='fa-sharp fa-solid fa-star'></i>
                        </li>
                      ))}
                    </ul>
                    {offer.hero?.subtitle || 'TRUSTED BY 500,000+ CUSTOMERS'}
                  </div>
                  <h1 className={`${styles['h1__title']} ${styles['hero__right-title']}`}>
                    {offer.hero?.title || 'Anyone Can Now Enjoy Freedom From Knee Aches, Pains and Injuries.'}
                  </h1>
                  <ul className={styles['hero__right-list']}>
                    {(
                      offer.hero?.bullets || [
                        {
                          bold: 'Fast Relief:',
                          text: 'Aligns your knee joint so you feel relief almost instantly.',
                        },
                        {
                          bold: 'Complete Support:',
                          text: 'The durable ultranet fabric and side bolstering brace the sides of your knee for a flexible, full range of motion.',
                        },
                        {
                          bold: 'Slim & Discreet:',
                          text: 'Designed to go unnoticed. Its sleek design is ultra slim and will fit discreetly under most pants',
                        },
                      ]
                    ).map((item, index) => (
                      <li key={index}>
                        <i className='fa-sharp fa-solid fa-arrow-right'></i>
                        <div className={styles['hero__right-list-text']}>
                          {item.bold && <b>{item.bold}</b>} {item.text}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Countdown Section */}
        <section className={styles['countdown']}>
          <div className={styles['container']}>
            <div className={styles['countdown__container']}>
              <div className={styles['countdown__wrap']}>
                <i className='fa-solid fa-triangle-exclamation'></i>
                <div className={styles['countdown__text']}>
                  LIMITED STOCK!
                  <span className={styles['countdown__black']}>
                    {offer.brand || 'Prepared Hero'} has been in demand and inventory is limited. Cart
                    reserved for{' '}
                  </span>
                  <span className={styles['js__countdown']}>{formatTime(timeLeft)}</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Offer Section */}
        <section className={styles['offer']}>
          <div className={styles['container']}>
            <div className={styles['offer__container']}>
              <div className={styles['offer__wrap']}>
                {/* LEFT COL */}
                <div className={styles['offer__left']}>
                  {/* STEP 1: PICK YOUR BUNDLE DEAL */}
                  <div className={`${styles['offer__step']} ${styles['offer__step-1']}`}>
                    <h3 className={`${styles['h3__title']} ${styles['offer__step-title']}`}>
                      STEP 1: PICK YOUR BUNDLE DEAL
                    </h3>
                    <div className={styles['offer__step-header']}>
                      <div className={styles['offer__step-subtitle']}>Product</div>
                      <div className={styles['offer__step-subtitle']}>Price</div>
                    </div>
                    <div className={styles['offer__step-cards']}>
                      {mergedProductsData.map((product) => (
                        <label
                          key={product.id}
                          htmlFor={product.id}
                          className={`${styles['offer__step-card']} ${
                            product.mostPopular ? styles['offer__step-card-highlight'] : ''
                          } ${product.soldOut === '1' ? styles['product__sold-out'] : ''}`}
                          onClick={() => product.soldOut !== '1' && handleProductSelect(product)}
                        >
                          <div className={styles['offer__step-card-product']}>
                            <input
                              type='radio'
                              name='selectedProduct'
                              id={product.id}
                              className={styles['offer__step-card-radio']}
                              checked={selectedProduct?.id === product.id}
                              onChange={() => {}} // Prevent default change, handle via label click
                              disabled={product.soldOut === '1'}
                            />
                            <div className={styles['offer__step-card-img']}>
                              <img src={product.image} alt={product.name} />
                            </div>
                            <div className={styles['offer__step-card-label']}>
                              {product.mostPopular && (
                                <div className={styles['offer__step-card-tag']}>Most Popular 🔥</div>
                              )}
                              {product.bestValue && (
                                <div className={styles['offer__step-card-tag']}>Best Value</div>
                              )}
                              {product.lowestPrice && (
                                <div className={styles['offer__step-card-tag']}>Lowest Price Ever</div>
                              )}
                              {product.quantity * 1} {makePlural(offer.productName, product.quantity * 1)}
                              {product.soldOut === '1' && (
                                <span style={{ color: 'red', marginLeft: '5px' }}>(Sold Out)</span>
                              )}
                            </div>
                          </div>
                          <div className={styles['offer__step-card-numbers']}>
                            {product.oldPrice && (
                              <div className={styles['offer__step-card-old']}>
                                {getPriceWithCurrency(product.oldPrice, offer.locale, offer.currency)}
                              </div>
                            )}
                            {product.oldPrice && product.price && (
                              <div className={styles['offer__step-card-discount']}>
                                {getSavingsPercent(product.oldPrice, product.price)}% OFF
                              </div>
                            )}
                            <div className={styles['offer__step-card-price']}>
                              {getPriceWithCurrency(product.price, offer.locale, offer.currency)}
                            </div>
                            <div className={styles['offer__step-card-bonus']}>
                              {product.shippingPrice > 0
                                ? `+${getPriceWithCurrency(
                                    product.shippingPrice,
                                    offer.locale,
                                    offer.currency
                                  )} S&H`
                                : '+FREE SHIPPING'}
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                    {/* Cross-sell items - Map through all cross-sells */}
                    {crossSells.map((crossSell) => (
                      <div key={crossSell.productId} className={styles['offer__step-hook']}>
                        <div className={styles['offer__step-hook-img']}>
                          <img src={crossSell.imageSrc || hookImg} alt={crossSell.name} />
                        </div>
                        <label
                          htmlFor={`cross-sell-${crossSell.productId}`}
                          className={styles['offer__step-hook-info']}
                        >
                          <div className={styles['offer__step-hook-head']}>
                            <i className='fa-sharp fa-solid fa-arrow-right'></i>
                            <input
                              className={styles['offer__step-hook-checkbox']}
                              type='checkbox'
                              name={`cross-sell-${crossSell.productId}`}
                              id={`cross-sell-${crossSell.productId}`}
                              checked={state.offers.some(
                                (o) => o.product_id === crossSell.productId && o.isCrossSell
                              )}
                              onChange={() => handleCrossSellChange(crossSell)}
                            />
                            {`Yes, Add ${crossSell.title} To My Order!`}
                          </div>
                          <div className={styles['offer__step-hook-text']}>
                            {crossSell.description ||
                              `Add this complementary product to enhance your purchase. Only ${getPriceWithCurrency(
                                crossSell.price,
                                offer.locale,
                                offer.currency
                              )}`}
                          </div>
                        </label>
                      </div>
                    ))}
                  </div>

                  {/* STEP 2: CUSTOMER INFORMATION */}
                  <div className={`${styles['offer__step']} ${styles['offer__step-2']}`}>
                    <h3 className={`${styles['h3__title']} ${styles['offer__step-title']}`}>
                      STEP 2: CUSTOMER INFORMATION
                    </h3>
                    <div className={styles['offer__step-inputs']}>
                      <div className={styles['offer__step-inputs-double']}>
                        <div className={styles['input__wrapper']}>
                          <input
                            placeholder='First Name*'
                            type='text'
                            name='firstName'
                            id='first_name'
                            value={state.firstName}
                            onChange={handleInputChange}
                            onBlur={() => handleFieldBlur('firstName')}
                            className={`${styles['offer__step-inputs-input']} ${
                              errors.firstName ? styles['error__input'] : ''
                            } ${
                              state.firstName && !errors.firstName
                                ? styles['offer__payment-details-input-complete']
                                : ''
                            }`}
                          />
                          {renderErrorMessage('firstName', errors)}
                        </div>
                        <div className={styles['input__wrapper']}>
                          <input
                            placeholder='Last Name*'
                            type='text'
                            name='lastName'
                            id='last_name'
                            value={state.lastName}
                            onChange={handleInputChange}
                            onBlur={() => handleFieldBlur('lastName')}
                            className={`${styles['offer__step-inputs-input']} ${
                              errors.lastName ? styles['error__input'] : ''
                            } ${
                              state.lastName && !errors.lastName
                                ? styles['offer__payment-details-input-complete']
                                : ''
                            }`}
                          />
                          {renderErrorMessage('lastName', errors)}
                        </div>
                      </div>
                      <div className={styles['input__wrapper']}>
                        <input
                          placeholder='Best Email Address*'
                          type='email'
                          name='email'
                          id='email'
                          value={state.email}
                          onChange={handleInputChange}
                          onBlur={() => handleFieldBlur('email')}
                          className={`${styles['offer__step-inputs-input']} ${
                            errors.email ? styles['error__input'] : ''
                          } ${
                            state.email && !errors.email
                              ? styles['offer__payment-details-input-complete']
                              : ''
                          }`}
                        />
                        {renderErrorMessage('email', errors)}
                      </div>
                      <div className={styles['input__wrapper']}>
                        <input
                          placeholder='Phone Number (optional)'
                          type='tel'
                          name='phone'
                          id='phone'
                          // strip existing +1 or +44 for display
                          value={state.phone.replace(/^\+1|^\+44/, '')}
                          onChange={(e) => {
                            // determine prefix based on country
                            const prefix =
                              state.shippingCountry === 'GB'
                                ? '+44'
                                : state.shippingCountry === 'US' || state.shippingCountry === 'CA'
                                ? '+1'
                                : '';
                            // keep only digits
                            const digits = e.target.value.replace(/\D/g, '');
                            setState((prev) => ({
                              ...prev,
                              phone: `${prefix}${digits}`,
                            }));
                            setErrors((prev) => ({ ...prev, phone: null }));
                          }}
                          onBlur={() => handleFieldBlur('phone')}
                          className={`${styles['offer__step-inputs-input']} ${
                            errors.phone ? styles['error__input'] : ''
                          } ${
                            state.phone && !errors.phone
                              ? styles['offer__payment-details-input-complete']
                              : ''
                          }`}
                        />
                        {renderErrorMessage('phone', errors)}
                      </div>
                    </div>
                  </div>

                  {/* STEP 3: SHIPPING INFORMATION */}
                  <div className={`${styles['offer__step']} ${styles['offer__step-3']}`}>
                    <h3 className={`${styles['h3__title']} ${styles['offer__step-title']}`}>
                      STEP 3: SHIPPING INFORMATION
                    </h3>
                    <div className={styles['offer__step-inputs']}>
                      <div className={styles['input__wrapper']}>
                        <div className={styles['offer__step-select-wrap']}>
                          <select
                            className={`${styles['offer__step-select']} ${
                              errors.shippingCountry ? styles['error__input'] : ''
                            } `}
                            name='shippingCountry'
                            id='country'
                            value={state.shippingCountry}
                            onChange={handleInputChange}
                            onBlur={() => handleFieldBlur('shippingCountry')}
                          >
                            <option value='' disabled hidden>
                              Select Country*
                            </option>
                            {offer.shippingCountries.map((country) => (
                              <option key={country.id} value={country.id}>
                                {country.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        {renderErrorMessage('shippingCountry', errors)}
                      </div>
                      <div className={styles['input__wrapper']}>
                        <input
                          placeholder='Street Address*'
                          type='text'
                          name='shippingAddress1'
                          id='shipping_address'
                          value={state.shippingAddress1}
                          onChange={handleInputChange}
                          onBlur={() => handleFieldBlur('shippingAddress1')}
                          className={`${styles['offer__step-inputs-input']} ${
                            errors.shippingAddress1 ? styles['error__input'] : ''
                          } ${
                            state.shippingAddress1 && !errors.shippingAddress1
                              ? styles['offer__payment-details-input-complete']
                              : ''
                          }`}
                        />
                        {renderErrorMessage('shippingAddress1', errors)}
                      </div>
                      <div className={styles['offer__step-inputs-tripple']}>
                        <div className={styles['input__wrapper']}>
                          <input
                            placeholder='City*'
                            type='text'
                            name='shippingCity'
                            id='shipping_city'
                            value={state.shippingCity}
                            onChange={handleInputChange}
                            onBlur={() => handleFieldBlur('shippingCity')}
                            className={`${styles['offer__step-inputs-input']} ${
                              errors.shippingCity ? styles['error__input'] : ''
                            } ${
                              state.shippingCity && !errors.shippingCity
                                ? styles['offer__payment-details-input-complete']
                                : ''
                            }`}
                          />
                          {renderErrorMessage('shippingCity', errors)}
                        </div>
                        <div className={styles['input__wrapper']}>
                          {state.shippingCountry === 'US' || state.shippingCountry === 'CA' ? (
                            <div className={styles['offer__step-select-wrap']}>
                              <select
                                className={`${styles['offer__step-select']} ${
                                  errors.shippingState ? styles['error__input'] : ''
                                } `}
                                name='shippingState'
                                id='shipping_state'
                                value={state.shippingState}
                                onChange={(e) => {
                                  setState({ ...state, shippingState: e.target.value });
                                  setErrors((prevErrors) => ({ ...prevErrors, shippingState: null }));
                                }}
                                onBlur={() => handleFieldBlur('shippingState')}
                              >
                                <option value='' disabled hidden>
                                  State / Province*
                                </option>
                                {Object.entries(states[state.shippingCountry] || {}).map(([code, name]) => (
                                  <option key={code} value={code}>
                                    {name}
                                  </option>
                                ))}
                              </select>
                              {renderErrorMessage('shippingState', errors)}
                            </div>
                          ) : (
                            <>
                              <input
                                placeholder='State / Province*'
                                type='text'
                                name='shippingState'
                                id='shipping_state'
                                value={state.shippingState}
                                onChange={(e) => {
                                  setState({ ...state, shippingState: e.target.value });
                                  setErrors((prevErrors) => ({ ...prevErrors, shippingState: null }));
                                }}
                                onBlur={() => handleFieldBlur('shippingState')}
                                className={`${styles['offer__step-inputs-input']} ${
                                  errors.shippingState ? styles['error__input'] : ''
                                } ${
                                  state.shippingState && !errors.shippingState
                                    ? styles['offer__payment-details-input-complete']
                                    : ''
                                }`}
                              />
                              {renderErrorMessage('shippingState', errors)}
                            </>
                          )}
                        </div>
                        <div className={styles['input__wrapper']}>
                          <input
                            placeholder='Zip / Postcode*'
                            type='text'
                            name='shippingZip'
                            id='shipping_zip'
                            value={state.shippingZip}
                            onChange={handleInputChange}
                            onBlur={() => handleFieldBlur('shippingZip')}
                            className={`${styles['offer__step-inputs-input']} ${
                              errors.shippingZip ? styles['error__input'] : ''
                            } ${
                              state.shippingZip && !errors.shippingZip
                                ? styles['offer__payment-details-input-complete']
                                : ''
                            }`}
                          />
                          {renderErrorMessage('shippingZip', errors)}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* SafeGuard Shipping Guarantee */}
                  {offer.showShippingGuarantee && (
                    <label htmlFor='SafeGuard' className={styles['offer__safe-guard']}>
                      <input
                        type='checkbox'
                        name='SafeGuard'
                        id='SafeGuard'
                        className={styles['offer__safe-guard-checkbox']}
                        checked={addShippingGuarantee}
                        onChange={(e) => setAddShippingGuarantee(e.target.checked)}
                      />
                      <div className={styles['offer__safe-guard-text']}>
                        Get peace of mind with SafeGuard Shipping Guarantee in the event your delivery is
                        damaged, stolen, or lost during transit for $1.99
                      </div>
                    </label>
                  )}
                </div>

                {/* RIGHT COL - ORDER SUMMARY & PAYMENT */}
                <div className={styles['offer__right']}>
                  <h3 className={`${styles['h3__title']} ${styles['offer__step-title']}`}>ORDER</h3>
                  {/* Order Summary */}
                  <div className={styles['offer__summary']}>
                    <div className={styles['offer__summary-header']}>
                      <div className={styles['offer__summary-subtitle']}>Product</div>
                      <div className={styles['offer__summary-subtitle']}>Price</div>
                    </div>
                    <div className={styles['offer__summary-items']}>
                      {/* Render all items from state.offers */}
                      {state.offers.map((item, index) => {
                        const productDetails = item.isMainProduct
                          ? selectedProduct
                          : crossSells.find((cs) => cs.productId === item.product_id) || {};
                        const image = item.isMainProduct
                          ? selectedProduct?.image
                          : productDetails?.imageSrc || hookImg;

                        return (
                          <div key={`${item.product_id}-${index}`} className={styles['offer__summary-item']}>
                            <div className={styles['offer__summary-img']}>
                              <img src={image} alt={item.productName} />
                              <div className={styles['offer__summary-quantity']}>{item.productQty}</div>
                            </div>
                            <div className={styles['offer__summary-name']}>
                              {item.productName.replace('(XL)', '')}
                            </div>
                            <div className={styles['offer__summary-info']}>
                              <div className={styles['offer__step-card-price']}>
                                {getPriceWithCurrency(item.price, offer.locale, offer.currency)}
                              </div>
                              {item.isMainProduct && selectedProduct?.shippingPrice > 0 && (
                                <div className={styles['offer__step-card-bonus']}>
                                  +
                                  {getPriceWithCurrency(
                                    selectedProduct.shippingPrice,
                                    offer.locale,
                                    offer.currency
                                  )}{' '}
                                  S&H
                                </div>
                              )}
                              {item.isMainProduct && selectedProduct?.shippingPrice <= 0 && (
                                <div className={styles['offer__step-card-bonus']}>+FREE SHIPPING</div>
                              )}
                            </div>
                          </div>
                        );
                      })}

                      {/* Shipping Guarantee (if added) */}
                      {addShippingGuarantee && (
                        <div className={styles['offer__summary-item']}>
                          <div className={styles['offer__summary-img']}>
                            <img src={shippingProtectionImg} alt='Shipping Protection' />
                            <div className={styles['offer__summary-quantity']}>1</div>
                          </div>
                          <div className={styles['offer__summary-name']}>Shipping Protection</div>
                          <div className={styles['offer__summary-info']}>
                            <div className={styles['offer__step-card-bonus']}>
                              {getPriceWithCurrency(1.99, offer.locale, offer.currency)}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className={styles['offer__summary-total']}>
                      <div className={styles['offer__summary-subtitle']}>Total</div>
                      <div className={styles['offer__summary-final']}>
                        {getPriceWithCurrency(state.orderTotal, offer.locale, offer.currency)}
                      </div>
                    </div>
                  </div>

                  {/* STEP 4: PAYMENT METHOD */}
                  <div className={`${styles['offer__step']} ${styles['offer__step-4']}`}>
                    <h3 className={`${styles['h3__title']} ${styles['offer__step-title']}`}>
                      STEP 4: PAYMENT METHOD
                    </h3>
                    <h3 className={styles['offer__step-desc']}>All transactions are secure and encrypted.</h3>

                    <div className={`${styles['offer__payment']}`}>
                      <div className={styles['offer__payment-options']}>
                        {/* Credit Card Option */}
                        <div className={styles['offer__payment-option']}>
                          <label htmlFor='checkout-card' className={styles['offer__payment-header']}>
                            <input
                              className={styles['offer__payment-radio']}
                              type='radio'
                              name='checkout'
                              id='checkout-card'
                              checked={paymentMethod === 'card'}
                              onChange={handlePaymentMethodChange}
                            />
                            <div className={styles['offer__payment-header-text']}>Credit/Debit Card</div>
                            <div className={styles['offer__payment-cards']}>
                              <img src={visaImg} alt='Visa' />
                              <img src={mastercardImg} alt='Mastercard' />
                              {offer.currency !== 'GBP' && <img src={amexImg} alt='Amex' />}
                            </div>
                          </label>
                          {paymentMethod === 'card' && (
                            <div className={styles['offer__payment-details']}>
                              <div className={styles['input__wrapper']}>
                                <label
                                  className={styles['offer__payment-details-label']}
                                  htmlFor='creditCardNumber'
                                >
                                  Card Number
                                </label>
                                <NumberFormat
                                  format='#### #### #### ####'
                                  mask=' '
                                  placeholder='1234 1234 1234 1234'
                                  name='creditCardNumber'
                                  id='creditCardNumber'
                                  value={state.creditCardNumber}
                                  onValueChange={(values) =>
                                    handleNumberFormatChange(values, 'creditCardNumber')
                                  }
                                  onBlur={() => handleFieldBlur('creditCardNumber')}
                                  className={`${styles['offer__payment-details-input']} ${
                                    errors.creditCardNumber ? styles['error__input'] : ''
                                  } ${
                                    state.creditCardNumber && !errors.creditCardNumber
                                      ? styles['offer__payment-details-input-complete']
                                      : ''
                                  }`}
                                />
                                {renderErrorMessage('creditCardNumber', errors)}
                              </div>

                              <div className={styles['offer__payment-details-triple']}>
                                <div className={styles['input__wrapper']}>
                                  <label
                                    className={styles['offer__payment-details-label']}
                                    htmlFor='expirationMonth'
                                  >
                                    Exp. Month
                                  </label>
                                  <select
                                    name='expirationMonth'
                                    id='expirationMonth'
                                    value={state.expirationMonth}
                                    onChange={handleInputChange}
                                    onBlur={() => handleFieldBlur('expirationMonth')}
                                    className={`${styles['offer__payment-details-input']} ${
                                      styles['offer__step-select']
                                    } ${errors.expirationMonth ? styles['error__input'] : ''} ${
                                      state.expirationMonth && !errors.expirationMonth
                                        ? styles['offer__payment-details-input-complete']
                                        : ''
                                    }`}
                                  >
                                    <option value='' disabled>
                                      MM
                                    </option>
                                    {Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0')).map(
                                      (month) => (
                                        <option key={month} value={month}>
                                          {month}
                                        </option>
                                      )
                                    )}
                                  </select>
                                  {renderErrorMessage('expirationMonth', errors)}
                                </div>

                                <div className={styles['input__wrapper']}>
                                  <label
                                    className={styles['offer__payment-details-label']}
                                    htmlFor='expirationYear'
                                  >
                                    Exp. Year
                                  </label>
                                  <select
                                    name='expirationYear'
                                    id='expirationYear'
                                    value={state.expirationYear}
                                    onChange={handleInputChange}
                                    onBlur={() => handleFieldBlur('expirationYear')}
                                    className={`${styles['offer__payment-details-input']} ${
                                      styles['offer__step-select']
                                    } ${errors.expirationYear ? styles['error__input'] : ''} ${
                                      state.expirationYear && !errors.expirationYear
                                        ? styles['offer__payment-details-input-complete']
                                        : ''
                                    }`}
                                  >
                                    <option value='' disabled>
                                      YYYY
                                    </option>
                                    {yearOptions.map((year) => (
                                      <option key={year} value={year}>
                                        {year}
                                      </option>
                                    ))}
                                  </select>
                                  {renderErrorMessage('expirationYear', errors)}
                                </div>

                                <div className={styles['input__wrapper']}>
                                  <label className={styles['offer__payment-details-label']} htmlFor='CVV'>
                                    CVC
                                  </label>
                                  <NumberFormat
                                    format='####'
                                    mask=' '
                                    placeholder='CVC'
                                    name='CVV'
                                    id='CVV'
                                    value={state.CVV}
                                    onValueChange={(values) => handleNumberFormatChange(values, 'CVV')}
                                    onBlur={() => handleFieldBlur('CVV')}
                                    className={`${styles['offer__payment-details-input']} ${
                                      errors.CVV ? styles['error__input'] : ''
                                    } ${
                                      state.CVV && !errors.CVV
                                        ? styles['offer__payment-details-input-complete']
                                        : ''
                                    }`}
                                  />
                                  {renderErrorMessage('CVV', errors)}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* PayPal Option */}
                        {showPayPal && !isPayPalLoading && isPayPalEnabled && (
                          <div className={styles['offer__payment-option']}>
                            <label htmlFor='checkout-paypal' className={styles['offer__payment-header']}>
                              <input
                                className={styles['offer__payment-radio']}
                                type='radio'
                                name='checkout'
                                id='checkout-paypal'
                                checked={paymentMethod === 'paypal'}
                                onChange={handlePaymentMethodChange}
                              />
                              <div className={styles['offer__payment-logo']}>
                                <img src={paypalImg} alt='PayPal' />
                              </div>
                            </label>
                            {paymentMethod === 'paypal' && (
                              <div className={styles['offer__payment-details']}>
                                <PayPalErrorBoundary>
                                  <PayPalScriptProvider
                                    options={{
                                      vault: true,
                                      clientId: process.env.REACT_APP_PAYPAL_CLIENT_ID,
                                      components: 'buttons',
                                      dataClientToken: clientToken,
                                    }}
                                  >
                                    <PayPalButtons
                                      offer={offer}
                                      orderData={state}
                                      checkoutTotal={state.orderTotal}
                                      onApprove={handleExpressApprove}
                                    />
                                  </PayPalScriptProvider>
                                </PayPalErrorBoundary>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Submit Button */}
                    <button
                      className={styles['offer__payment-button']}
                      onClick={handleFormSubmit}
                      disabled={isLoading || isCooldownActive || paymentMethod === 'paypal'}
                    >
                      <div className={styles['offer__payment-button-top']}>
                        COMPLETE PURCHASE
                        <i className='fa-sharp fa-solid fa-arrow-right'></i>
                        {isLoading && (
                          <i className='fas fa-spinner fa-spin' style={{ marginLeft: '10px' }}></i>
                        )}
                      </div>
                      <div className={styles['offer__payment-button-button']}>
                        TRY IT RISK FREE! - 30 DAY MONEY BACK GUARANTEE!
                      </div>
                    </button>

                    {/* Decline/Cooldown/Form Errors */}
                    {isCooldownActive && (
                      <div className={styles['decline__error']}>
                        <p>
                          You've had multiple declined payment attempts. Please wait 15 minutes before trying
                          again or contact customer support.
                        </p>
                      </div>
                    )}
                    {declineError && !isCooldownActive && (
                      <div className={styles['decline__error']}>
                        <p>{declineError}</p>
                      </div>
                    )}
                    {errors.form && (
                      <div className={styles['decline__error']}>
                        <p>{errors.form}</p>
                      </div>
                    )}
                    {errors.product && (
                      <div className={styles['decline__error']}>
                        <p>{errors.product}</p>
                      </div>
                    )}

                    {/* Security Logos & Guarantee */}
                    <div className={styles['offer__logos']}>
                      <img
                        className={styles['offer__logos-img']}
                        src={checkoutSecuredImg}
                        alt='Checkout Secured'
                      />
                      <ul className={styles['offer__logos-list']}>
                        <li>
                          <img src={amexImg} alt='Amex' />
                        </li>
                        <li>
                          <img src={mastercardImg} alt='Mastercard' />
                        </li>
                        <li>
                          <img src={visaImg} alt='Visa' />
                        </li>
                      </ul>
                      <div className={styles['offer__logos-text']}>
                        <i className='fa-solid fa-lock-keyhole'></i> Secure 256-bit SSL encryption
                      </div>
                    </div>
                    <div className={styles['offer__guarantee']}>
                      <img src={guaranteeImg} alt='Guarantee' />
                      <div className={styles['offer__guarantee-text']}>
                        <div className={styles['offer__guarantee-title']}>30 Day HERO Guarantee</div>
                        <div className={styles['offer__guarantee-desc']}>
                          Try it risk-free today! Don't LOVE it? Simply send it back for a full refund.
                        </div>
                      </div>
                    </div>

                    {/* Testimonials (Static Example) */}
                    <div className={styles['offer__testimonials']}>
                      <div className={styles['offer__testimonials-title']}>
                        What Customers Are Saying About Us:
                      </div>
                      <div className={styles['offer__testimonials-wrap']}>
                        {reviews.map((review, index) => (
                          <div key={index} className={styles['offer__testimonials-item']}>
                            <img
                              className={styles['offer__testimonials-photo']}
                              src={review.imageSrc}
                              alt={`Testimonial ${index + 1}`}
                            />
                            <div className={styles['offer__testimonials-info']}>
                              <div className={styles['offer__testimonials-author']}>
                                <div className={styles['offer__testimonials-name']}>{review.name}</div>
                                <div className={styles['offer__testimonials-comment']}>{review.text}</div>
                              </div>
                              <div className={styles['offer__testimonials-bottom']}>
                                <div className={styles['offer__testimonials-like']}>Like</div>
                                <div className={styles['offer__testimonials-reply']}>Reply</div>
                                <div className={styles['offer__testimonials-date']}>2w</div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className={styles['faq']}>
          <div className={styles['container']}>
            <div className={styles['faq__container']}>
              <h2 className={`${styles['h2__title']} ${styles['faq__title']}`}>
                Have a Question? We Can Help!
              </h2>
              <div className={styles['faq__accordion']}>
                {faqData.map((item, index) => (
                  <div
                    key={index}
                    className={`${styles['faq__accordion-item']} ${
                      activeFaqIndex === index ? styles['faq__accordion-item-active'] : ''
                    }`}
                  >
                    <h4 className={styles['faq__accordion-toggle']} onClick={() => handleFaqToggle(index)}>
                      {item.question}
                      <i className='fa-solid fa-chevron-down'></i>
                    </h4>
                    <div
                      className={`${styles['faq__accordion-content']} ${
                        activeFaqIndex === index ? styles['faq__accordion-content-active'] : ''
                      }`}
                    >
                      <p>{item.answer}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Optional Reviews Component */}
        {showReviews && reviews && <Reviews reviews={reviews} />}
      </main>
    </div>
  );
}
